import { useState } from 'react';
import { useTranslate } from 'react-polyglot';
import { useTheme } from 'styled-components';

import {
  BasicTitle,
  BiggerSubtitle,
  Breakpoint,
  Button,
  Input,
  ModalContainer,
  PixelsPerBreakpoint,
  useBreakpoint,
} from '@genially/design-system';
import { PlayP2pClient, UsernameInUseClaimError } from '@genially/p2p-client';
import { UserAliasAvatar } from '@genially/ui/build/components/UserAliasAvatar/UserAliasAvatar';
import { decodeError } from '@genially/ui/build/utils/decodeError';
import { calculate } from '@genially/utils/build/module';

import { UsernameIsTakenError } from '../../../../../../../../shared/application/components/UsernameIsTakenError/UsernameIsTakenError';
import { getLogger } from '../../../../../../../../shared/infrastructure/logger';
import {
  Card,
  ContentWrapper,
  ErrorWrapper,
  StyledArrowEndPointIcon,
  StyledResponsiveText,
  UsernameSelectionWrapper,
} from './PlayerAliasModal.styled';

const UserAliasMaxLength = 40;

export interface PlayerAliasModalProps {
  show: boolean;
  dismissable: boolean;
  onClose: () => void;
  onSubmit: (username: string) => Promise<void>;
}

export const PlayerAliasModal = ({ show, onClose, onSubmit }: PlayerAliasModalProps) => {
  const t = useTranslate();
  const theme = useTheme();

  const [username, setUsername] = useState('');
  const trimmedUsername = username.trim();

  const [checking, setChecking] = useState(false);
  const [isUsernameInUse, setIsUsernameInUse] = useState(false);

  const { isDownwards } = useBreakpoint();
  const isResponsive = isDownwards(Breakpoint.xs);
  const hasSquareBottomCorners = isDownwards(PixelsPerBreakpoint[Breakpoint.ipad] - 1);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      setUsername('');
      return;
    }

    setIsUsernameInUse(false);

    const newUsername = inputValue.replace(/-/g, '');
    setUsername(newUsername);
  };

  const handleClick = async () => {
    if (!PlayP2pClient.isValidUsername(trimmedUsername)) {
      return;
    }

    setChecking(true);

    try {
      await onSubmit(trimmedUsername);

      setUsername('');
      onClose();
    } catch (error) {
      if (error instanceof UsernameInUseClaimError) {
        setIsUsernameInUse(true);
      } else {
        getLogger().error('Error claiming username', { error: decodeError(error) });
      }
    } finally {
      setChecking(false);
    }
  };

  const handleEnter = () => {
    if (!checking) {
      handleClick();
    }
  };

  const ErrorComponent = calculate(() => {
    if (isUsernameInUse) {
      return <UsernameIsTakenError />;
    }

    return undefined;
  });

  if (!show) {
    return null;
  }

  const cardBorderRadius = theme.size.units(5);
  const borderRadius = hasSquareBottomCorners
    ? `${cardBorderRadius} ${cardBorderRadius} 0 0`
    : cardBorderRadius;

  return (
    <ModalContainer
      dataCy="alias-modal"
      width="100%"
      maxWidth={theme.size.units(69)}
      bgColor={theme.color.background.reversed.default()}
      borderRadius={borderRadius}
    >
      <Card>
        <StyledResponsiveText with={BasicTitle} on={{ xs: BiggerSubtitle }}>
          {t('__new.play.playerAccess.username.title')}
        </StyledResponsiveText>

        <ContentWrapper>
          <UsernameSelectionWrapper>
            <UserAliasAvatar name={username} size={UserAliasAvatar.Size.Big} />
            <Input
              autofocus
              blurOnEnter={false}
              size={Input.Size.BIG}
              placeholder="Ej: Javi Potter"
              value={username}
              maxLength={UserAliasMaxLength}
              onChange={handleUsernameChange}
              onEnter={handleEnter}
              status={ErrorComponent !== undefined ? Input.Status.ERROR : undefined}
            />

            {isResponsive && ErrorComponent !== undefined ? ErrorComponent : undefined}

            <Button
              variant={Button.Variant.CTA}
              size={Button.Size.BIG}
              width={isResponsive ? '100%' : undefined}
              disabled={checking || username.length === 0}
              onClick={handleClick}
              loading={checking}
            >
              <StyledArrowEndPointIcon />
            </Button>
          </UsernameSelectionWrapper>

          {!isResponsive && ErrorComponent !== undefined ? (
            <ErrorWrapper>{ErrorComponent}</ErrorWrapper>
          ) : undefined}
        </ContentWrapper>
      </Card>
    </ModalContainer>
  );
};
